import { useId } from "react";
import type * as React from "react";

import { type ChipTone, toneFill } from "./Chip";
import { PatientSprites } from "./PatientSprites";
import type { SpriteTraits } from "./sprites";
import { cn } from "../lib/cn";

export interface BoardDelay {
  minutes: number;
  direction: "behind" | "ahead";
}

const delayText = (minutes: number): string =>
  minutes >= 1440
    ? `${Math.floor(minutes / 1440)}d`
    : minutes >= 60
      ? `${Math.floor(minutes / 60)}h`
      : `${minutes}m`;

function delayTone(delay: BoardDelay): ChipTone {
  if (delay.direction === "ahead") return "recorded";
  return delay.minutes > 10 ? "declined" : "attention";
}

export interface BoardCardPet {
  key: string;
  name: string;
  onOpen?: () => void;
}

export interface BoardCardProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "children" | "onSelect"
> {
  name: React.ReactNode;
  /** A short fact beside the name in annotation type: the sibling elsewhere ("with Tofu"). */
  mark?: React.ReactNode;
  /** A household card's pets under the label, each its own target when given `onOpen`. */
  pets?: readonly BoardCardPet[];
  who?: React.ReactNode;
  time?: React.ReactNode;
  /** One per pet on the card, stacked past the first. */
  sprites: readonly (SpriteTraits | null | undefined)[];
  delay?: BoardDelay;
  onSelect?: React.MouseEventHandler<HTMLButtonElement>;
  label?: string;
  ref?: React.Ref<HTMLElement>;
}

const PADDING = "px-3.5 py-3";

export function BoardCard({
  name,
  mark,
  pets,
  who,
  time,
  sprites,
  delay,
  onSelect,
  label,
  className,
  ...rest
}: BoardCardProps) {
  const behind = delay?.direction === "behind";
  const nameId = useId();

  const face = (
    <>
      <span className="flex items-center gap-2.5">
        <PatientSprites sprites={sprites} size={28} />
        <span
          id={nameId}
          className="min-w-0 font-display text-[1.1875rem] font-medium leading-[1.15] tracking-[-0.01em] text-ink [overflow-wrap:anywhere]"
        >
          {name}
          {mark != null && (
            <span className="ml-1.5 whitespace-nowrap font-body text-[0.8125rem] font-normal tracking-normal text-annotation">
              {mark}
            </span>
          )}
        </span>
      </span>
      {pets && pets.length > 0 && (
        <span className="mt-[3px] block font-body text-[0.8125rem] text-ink-body">
          {pets.map((pet, i) => (
            <span key={pet.key}>
              {i > 0 && ", "}
              {pet.onOpen ? (
                <button
                  type="button"
                  onClick={pet.onOpen}
                  className="pointer-events-auto relative cursor-pointer underline decoration-dotted underline-offset-2 hover:text-ink"
                >
                  {pet.name}
                </button>
              ) : (
                pet.name
              )}
            </span>
          ))}
        </span>
      )}
      {who != null && (
        <span className="mt-[3px] block font-body text-[0.8125rem] text-annotation">
          {who}
        </span>
      )}
      <span className="mt-2.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border-t border-dotted border-rule pt-2">
        <span className="min-w-0 break-words text-mono-time text-xs text-ink-body">
          {time}
        </span>
        {delay && (
          <span
            className={cn(
              "whitespace-nowrap rounded-tag px-2 py-[3px] font-mono text-fine-print font-medium tracking-[0.06em] text-ink",
              toneFill[delayTone(delay)],
            )}
          >
            {delayText(delay.minutes)} {delay.direction}
          </span>
        )}
      </span>
    </>
  );

  return (
    <article
      className={cn(
        "relative rounded-paper border border-rule shadow-board",
        behind ? "bg-tint-attention" : "bg-card",
        onSelect &&
          "transition duration-150 ease-paper hover:-translate-y-px hover:shadow-lift-lg motion-reduce:transition-none",
        className,
      )}
      {...rest}
    >
      {onSelect && (
        <button
          type="button"
          onClick={onSelect}
          aria-label={label}
          aria-labelledby={label ? undefined : nameId}
          className="absolute inset-0 cursor-pointer rounded-paper focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-accent"
        />
      )}
      {/* The button lies under the face so a control in `who` (`pointer-events-auto relative`) stays its own target. */}
      <span
        className={cn(
          "flex flex-col",
          PADDING,
          onSelect && "pointer-events-none relative",
        )}
      >
        {face}
      </span>
    </article>
  );
}
